import { useState, useEffect } from "react";

const FONTS = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400;1,600&family=Caveat:wght@400;600;700&family=Lora:ital,wght@0,400;1,400;1,500&display=swap');
  `}</style>
);

const C = {
  manila:   "#F5F0E8",
  manilaDk: "#EDE7D9",
  rose:     "#efd3d7",
  ink:      "#2C2438",
  sepia:    "#8e9aaf",
  sage:     "#cbc0d3",
  lavender: "#dee2ff",
};

const Bloom = ({ color = "#efd3d7", size = 28, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none" style={style}>
    <circle cx="14" cy="14" r="4" fill={color} opacity=".7"/>
    {[0,60,120,180,240,300].map(a => (
      <ellipse key={a}
        cx={14 + 8*Math.cos(a*Math.PI/180)} cy={14 + 8*Math.sin(a*Math.PI/180)}
        rx="4" ry="2.5"
        transform={`rotate(${a} ${14 + 8*Math.cos(a*Math.PI/180)} ${14 + 8*Math.sin(a*Math.PI/180)})`}
        fill={color} opacity=".3"/>
    ))}
  </svg>
);

function SiteNav() {
  return (
    <nav style={{
      position:"fixed", top:0, left:0, right:0, zIndex:200,
      background:"#F5F0E8", borderBottom:"1px solid #cbc0d370",
      transform:"translateZ(0)", WebkitTransform:"translateZ(0)",
      WebkitBackfaceVisibility:"hidden", backfaceVisibility:"hidden",
    }}>
      <div style={{
        maxWidth:"1200px", margin:"0 auto",
        display:"flex", alignItems:"center", justifyContent:"center",
        gap:"clamp(1.5rem, 6vw, 6rem)", padding:".75rem 2rem",
      }}>
        <a href="/"
          style={{ background:"none", border:"none", cursor:"pointer",
            color:"#5a5068", padding:".3rem .5rem", display:"flex", alignItems:"center",
            touchAction:"manipulation", flexShrink:0, textDecoration:"none" }}
          title="Home"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z"/>
            <path d="M9 21V12h6v9"/>
          </svg>
        </a>
        {["Photos","Memories","Livestream"].map(l => (
          <a key={l} href="/"
            style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
              fontSize:"clamp(.95rem, 2vw, 1rem)", color:"#5a5068",
              background:"none", border:"none", cursor:"pointer",
              padding:".3rem", fontWeight:600, whiteSpace:"nowrap",
              touchAction:"manipulation", letterSpacing:".02em",
              textDecoration:"none" }}>
            {l}
          </a>
        ))}
      </div>
    </nav>
  );
}

export default function EditMemoryPage() {
  const token = new URLSearchParams(window.location.search).get("token");

  const [state, setState] = useState("loading");
  const [form, setForm] = useState({ name: "", text: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!token) { setState("notfound"); return; }
    fetch(`/api/get-memory?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setState("notfound"); return; }
        setForm({ name: data.name, text: data.text });
        setState("edit");
      })
      .catch(() => setState("error"));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    setState("deleting");
    try {
      const res = await fetch("/api/delete-memory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.ok) {
        setState("deleted");
      } else {
        setErrorMsg(data.error || "Something went wrong. Please try again.");
        setState("edit");
      }
    } catch {
      setErrorMsg("Network error. Please try again.");
      setState("edit");
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.text.trim()) return;
    setState("saving");
    try {
      const res = await fetch("/api/update-memory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name: form.name, text: form.text, publish: true }),
      });
      const data = await res.json();
      if (data.ok) {
        setState("published");
      } else {
        setErrorMsg(data.error || "Something went wrong. Please try again.");
        setState("edit");
      }
    } catch {
      setErrorMsg("Network error. Please try again.");
      setState("edit");
    }
  };

  const inputStyle = {
    width:"100%", padding:".65rem .9rem",
    border:"1.5px solid #cbc0d3", borderRadius:"5px",
    background:C.manila, fontSize:".92rem",
    color:C.ink, fontStyle:"italic", outline:"none",
    fontFamily:"'Lora',serif",
  };

  const allFilled = form.name.trim() && form.text.trim();

  return (
    <div style={{ background:C.manila, minHeight:"100vh", fontFamily:"'Lora',serif",
      paddingTop:"52px", overflowX:"hidden" }}>
      {FONTS}
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        body { background:#F5F0E8; }
        textarea, input { font-family:'Lora',serif; font-size:16px; }
        button { touch-action:manipulation; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        .fadein { animation: fadeUp .7s ease both; }
      `}</style>

      <SiteNav />

      <div style={{ maxWidth:"600px", margin:"0 auto", padding:"4rem 2rem 5rem" }}>

        <div style={{ textAlign:"center", padding:"0 0 2.5rem" }}>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
            fontSize:"clamp(2rem,5vw,3.2rem)", color:C.ink, marginBottom:".5rem" }}>
            Memories of Ronnie
          </h1>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"8px" }}>
            <div style={{ width:"40px", height:"1px", background:C.sage }}/>
            <Bloom color={C.rose} size={14}/>
            <div style={{ width:"40px", height:"1px", background:C.sage }}/>
          </div>
        </div>

        {state === "loading" && (
          <p style={{ textAlign:"center", fontFamily:"'Lora',serif", fontStyle:"italic",
            color:C.sepia, padding:"4rem 0" }}>Loading…</p>
        )}

        {state === "notfound" && (
          <div className="fadein" style={{ background:"#fff", borderRadius:"8px", padding:"2.5rem",
            boxShadow:"2px 4px 20px rgba(58,42,30,.09)", border:"1px solid #cbc0d360",
            textAlign:"center" }}>
            <Bloom color={C.sage} size={36} style={{ margin:"0 auto .75rem" }}/>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
              fontSize:"1.4rem", color:C.ink, marginBottom:".5rem" }}>Link not found</p>
            <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
              fontSize:".9rem", color:C.sepia, lineHeight:1.8 }}>
              This link may be invalid or expired. Please check your email and try again.
            </p>
          </div>
        )}

        {state === "already-published" && (
          <div className="fadein" style={{ background:C.lavender, borderRadius:"8px", padding:"2.5rem",
            boxShadow:"2px 4px 20px rgba(58,42,30,.09)", border:"1px solid #cbc0d360",
            textAlign:"center" }}>
            <Bloom color={C.sage} size={42} style={{ margin:"0 auto .75rem" }}/>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
              fontSize:"1.4rem", color:C.ink, marginBottom:".5rem" }}>
              Your memory is already published.
            </p>
            <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
              fontSize:".9rem", color:C.sepia, lineHeight:1.8 }}>
              It's live on the website. Thank you for sharing.
            </p>
          </div>
        )}

        {state === "published" && (
          <div className="fadein" style={{ background:C.rose, borderRadius:"8px", padding:"2.5rem",
            boxShadow:"2px 4px 18px rgba(58,42,30,.08)", border:"1px solid #d4a8b0",
            textAlign:"center" }}>
            <Bloom color="#d4a8b0" size={42} style={{ margin:"0 auto .75rem" }}/>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
              fontSize:"1.4rem", color:C.ink, marginBottom:".5rem" }}>
              Thank you for sharing.
            </p>
            <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
              fontSize:".9rem", color:"#5a3a40", lineHeight:1.8 }}>
              Your memory of Ronnie has been updated on the website.
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="fadein" style={{ background:"#fff", borderRadius:"8px", padding:"2.5rem",
            boxShadow:"2px 4px 20px rgba(58,42,30,.09)", border:"1px solid #cbc0d360",
            textAlign:"center" }}>
            <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
              fontSize:".9rem", color:C.sepia }}>
              Something went wrong. Please try refreshing the page.
            </p>
          </div>
        )}

        {state === "deleted" && (
          <div className="fadein" style={{ textAlign:"center", padding:"2rem 0" }}>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
              fontSize:"1.4rem", color:C.ink, marginBottom:".5rem" }}>
              Your memory has been removed.
            </p>
            <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
              fontSize:".9rem", color:C.sepia, lineHeight:"1.8" }}>
              Thank you for sharing your memory of Ronnie.
            </p>
          </div>
        )}

        {(state === "edit" || state === "saving" || state === "deleting") && (
          <div className="fadein">

            <div style={{ background:C.lavender, borderRadius:"8px", padding:"1.25rem 1.6rem",
              marginBottom:"1.75rem", border:"1px solid #cbc0d360" }}>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic", fontWeight:600,
                fontSize:"1.15rem", color:C.ink, marginBottom:".4rem" }}>
                You're editing your memory
              </p>
              <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
                fontSize:".88rem", color:"#5a5068", lineHeight:1.8, margin:0 }}>
                Your memory is already live on ronnieeldridge.com. Read it over and make any changes you'd like — your updates will go live immediately when you click <em>Save and update</em>.
              </p>
            </div>

            <div style={{ background:"#fff", borderRadius:"8px", padding:"2.5rem",
              boxShadow:"2px 4px 20px rgba(58,42,30,.09)", border:"1px solid #cbc0d360" }}>

              {errorMsg && (
                <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".85rem",
                  color:"#c0392b", marginBottom:"1rem" }}>{errorMsg}</p>
              )}

              <div style={{ marginBottom:"1.25rem" }}>
                <label style={{ display:"block", fontFamily:"'Caveat',cursive", fontWeight:600,
                  fontSize:"1rem", color:C.sepia, marginBottom:".35rem" }}>Your name</label>
                <input
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  style={inputStyle}
                  disabled={state === "saving" || state === "deleting"}
                  onFocus={e => e.target.style.borderColor = C.sepia}
                  onBlur={e => e.target.style.borderColor = C.sage}
                />
              </div>

              <div style={{ marginBottom:"1.5rem" }}>
                <label style={{ display:"block", fontFamily:"'Caveat',cursive", fontWeight:600,
                  fontSize:"1rem", color:C.sepia, marginBottom:".35rem" }}>Your memory</label>
                <textarea
                  value={form.text}
                  onChange={e => setForm(p => ({ ...p, text: e.target.value }))}
                  rows={9}
                  style={{ ...inputStyle, resize:"vertical" }}
                  disabled={state === "saving" || state === "deleting"}
                  onFocus={e => e.target.style.borderColor = C.sepia}
                  onBlur={e => e.target.style.borderColor = C.sage}
                />
              </div>

              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start", gap:"1rem" }}>
                <button
                  onClick={handleSave}
                  disabled={!allFilled || state === "saving" || state === "deleting"}
                  style={{
                    fontFamily:"'Caveat',cursive", fontWeight:700, fontSize:"1.05rem",
                    padding:".7rem 2rem", borderRadius:"40px",
                    background:"#cbc0d3", color:"#2C2438", border:"none",
                    cursor: (state === "saving" || state === "deleting") ? "default" : "pointer",
                    boxShadow:"none", transition:"all .2s",
                  }}>
                  {state === "saving" ? "Saving…" : "Save and update my memory"}
                </button>

                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={state === "saving" || state === "deleting"}
                  style={{
                    fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".82rem",
                    background:"none", border:"none", color:"#b08a8a",
                    cursor:"pointer", textDecoration:"underline", padding:0,
                  }}>
                  Remove my memory
                </button>
              </div>

            </div>
          </div>
        )}

      </div>

      {confirmDelete && (
        <div style={{ position:"fixed", inset:0, background:"rgba(44,36,56,0.4)",
          display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#fff", borderRadius:"10px", padding:"2rem 2.5rem",
            maxWidth:"380px", width:"90%", textAlign:"center",
            boxShadow:"0 8px 40px rgba(44,36,56,0.18)" }}>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
              fontSize:"1.3rem", color:"#2C2438", marginBottom:".75rem" }}>
              Remove your memory?
            </p>
            <p style={{ fontFamily:"'Lora',serif", fontSize:".88rem",
              color:"#8e9aaf", lineHeight:"1.7", marginBottom:"1.75rem" }}>
              This can't be undone.
            </p>
            <div style={{ display:"flex", gap:"1rem", justifyContent:"center" }}>
              <button onClick={() => setConfirmDelete(false)}
                style={{ fontFamily:"'Caveat',cursive", fontWeight:700, fontSize:"1rem",
                  padding:".6rem 1.6rem", borderRadius:"40px",
                  background:"#F5F0E8", color:"#2C2438", border:"1px solid #cbc0d3",
                  cursor:"pointer" }}>
                Cancel
              </button>
              <button onClick={() => { setConfirmDelete(false); handleDelete(); }}
                style={{ fontFamily:"'Caveat',cursive", fontWeight:700, fontSize:"1rem",
                  padding:".6rem 1.6rem", borderRadius:"40px",
                  background:"#b08a8a", color:"#fff", border:"none",
                  cursor:"pointer" }}>
                Yes, remove it
              </button>
            </div>
          </div>
        </div>
      )}

      <footer style={{ background:C.manilaDk, borderTop:"1px solid #cbc0d360",
        padding:"3.5rem 2rem", textAlign:"center",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:".6rem" }}>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
          fontSize:".9rem", color:C.sepia, lineHeight:"1.5" }}>
          <span style={{ fontWeight:600, color:C.ink }}>Ronnie Eldridge</span><br/>
          January 30, 1931 – March 4, 2026
        </p>
        <Bloom color="#c9a8b8" size={36}/>
      </footer>
    </div>
  );
}